import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';

interface Props {
  direction: 'horizontal' | 'vertical';
  /** Initial size of the first pane, in pixels. */
  initial: number;
  min: number;
  max: number;
  /** Persisted across reloads under this key, so layout survives a refresh. */
  storageKey: string;
  first: ReactNode;
  second: ReactNode;
}

/**
 * A two-pane splitter with a draggable divider.
 *
 * Hand-rolled rather than pulled from a library because the requirement is
 * genuinely small, and a resize handle is a place where a dependency's
 * pointer-event handling tends to fight the editor's.
 */
export function SplitPane({
  direction,
  initial,
  min,
  max,
  storageKey,
  first,
  second,
}: Props) {
  const [size, setSize] = useState(() => {
    const saved = Number(localStorage.getItem(storageKey));
    return Number.isFinite(saved) && saved >= min && saved <= max ? saved : initial;
  });
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const horizontal = direction === 'horizontal';

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const raw = horizontal ? event.clientX - rect.left : event.clientY - rect.top;
      setSize(Math.min(max, Math.max(min, raw)));
    },
    [horizontal, min, max],
  );

  useEffect(() => {
    if (!dragging) return;

    const stop = () => setDragging(false);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stop);
    // The editor and grid would otherwise select text under the cursor while
    // the divider is being dragged.
    document.body.style.userSelect = 'none';
    document.body.style.cursor = horizontal ? 'col-resize' : 'row-resize';

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stop);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [dragging, onPointerMove, horizontal]);

  useEffect(() => {
    localStorage.setItem(storageKey, String(size));
  }, [size, storageKey]);

  return (
    <div
      ref={containerRef}
      className={clsx('flex h-full w-full', horizontal ? 'flex-row' : 'flex-col')}
    >
      <div
        className="min-h-0 min-w-0 overflow-hidden"
        style={horizontal ? { width: size, flexShrink: 0 } : { height: size, flexShrink: 0 }}
      >
        {first}
      </div>

      <div
        role="separator"
        aria-orientation={horizontal ? 'vertical' : 'horizontal'}
        tabIndex={0}
        className={clsx(
          'group relative shrink-0 bg-edge transition-colors hover:bg-accent/50',
          horizontal ? 'w-px cursor-col-resize' : 'h-px cursor-row-resize',
          dragging && 'bg-accent',
        )}
        onPointerDown={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onKeyDown={(event) => {
          // Keyboard resizing: a mouse-only splitter is an accessibility dead end.
          const step = event.shiftKey ? 40 : 12;
          if (event.key === (horizontal ? 'ArrowLeft' : 'ArrowUp')) {
            setSize((s) => Math.max(min, s - step));
          } else if (event.key === (horizontal ? 'ArrowRight' : 'ArrowDown')) {
            setSize((s) => Math.min(max, s + step));
          }
        }}
      >
        {/* A 1px divider is impossible to grab; this widens the hit area
            without widening the visible line. */}
        <span
          className={clsx(
            'absolute',
            horizontal ? '-left-1.5 -right-1.5 inset-y-0' : '-top-1.5 -bottom-1.5 inset-x-0',
          )}
        />
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{second}</div>
    </div>
  );
}
